/**
 * Bold — Bundle & Save quantity tiers.
 *
 * Renders the ladder from the JSON config the snippet emits and syncs the
 * selected tier to the product form's quantity field.
 *
 * DISPLAY ONLY: the theme cannot read Shopify's discount rules. A matching
 * automatic discount must exist or checkout charges full price. Nothing here
 * changes what is actually billed - it only sets quantity.
 *
 * "free_shipping": true on a tier shows the badge outright. It is trusted because
 * a merchant setting it is making a deliberate promise - but it still needs a
 * matching shipping rule in Shopify, exactly like the tier prices do.
 */
(() => {
  class BoldBundleSave extends HTMLElement {
    connectedCallback() {
      const raw = this.querySelector('[data-bundle-save-config]');
      if (!raw) return;
      try { this.cfg = JSON.parse(raw.textContent); } catch (_) { return; }

      this.row = this.querySelector('[data-bundle-save-row]');
      this.qtyEl = this.querySelector('[data-bundle-save-qty]');
      this.tiers = this.buildTiers();
      if (this.tiers.length < 2) return; // nothing to choose between

      this.selected = 0;
      this.unitsEl = this.querySelector('[data-bundle-save-units]');
      this.unitColours = [];          // chosen colour per unit slot
      this.form = document.querySelector(`#product-form-${this.dataset.sectionId}`)
        || document.querySelector('product-form form');
      this.render();
      this.hidden = false;

      this.row.addEventListener('click', e => {
        const btn = e.target.closest('[data-bundle-tier]');
        if (!btn) return;
        this.select(Number(btn.dataset.bundleTier));
      });

      // Per-unit colour picking.
      this.unitsEl?.addEventListener('click', e => {
        const sw = e.target.closest('[data-unit-swatch]');
        if (!sw) return;
        const slot = Number(sw.closest('[data-unit-slot]').dataset.unitSlot);
        this.pickColour(slot, sw.dataset.unitSwatch);
      });

      // Multi-colour bundles cannot go through the normal single-variant form.
      //
      // Bound on document in the CAPTURE phase, not on the form. product-form has
      // its own submit listener that posts the form data to /cart/add, and
      // preventDefault() does not stop it - a listener on the form would leave
      // both posts running and add the bundle twice. At the target both capture
      // and bubble listeners fire in registration order, so binding higher up is
      // what actually gets us in first; onSubmit then stops the event dead.
      document.addEventListener('submit', e => {
        if (e.target === this.form) this.onSubmit(e);
      }, true);
      this.row.addEventListener('keydown', e => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const step = e.key === 'ArrowRight' ? 1 : -1;
        this.select((this.selected + step + this.tiers.length) % this.tiers.length, true);
      });

      // A variant change rewrites the price, so the ladder has to be rebuilt.
      document.addEventListener('product-info:loaded', () => this.refresh());

      // ...but that event only fires when product-info itself connects, and a
      // variant change re-renders the block in place without reconnecting it.
      // The change listener below runs the moment the swatch is clicked, long
      // before the theme's fetch lands, so anything written then is thrown away
      // by the fresh markup - which carries quantity="1". Watching the block is
      // the only hook that fires after the replacement, whatever the network did.
      // syncQuantity writes only when the value differs, so re-entry stops here.
      const host = this.closest('product-info');
      if (host) {
        new MutationObserver(() => {
          this.syncQuantity(this.tiers[this.selected]?.qty ?? 1);
          this.mountUnits();
          // The theme's variant fetch re-renders the buy buttons with a bare
          // "Add to cart" label, often after the 120/350ms repaints below have
          // already run. Repainting here puts the price back whenever that lands.
          // paintAtc only writes on a real change, so this cannot feed itself.
          this.paintAtc();
        }).observe(host, { childList: true, subtree: true });
      }
      document.addEventListener('change', e => {
        if (!e.target.closest?.('.product-option__input')) return;
        requestAnimationFrame(() => this.refresh());
        // paintAtc stands down while bold-select-size is holding the button at
        // "Select size". Choosing a size is exactly when it lets go, so paint
        // again just after - otherwise the price never appears on the button.
        [120, 350].forEach(ms => setTimeout(() => this.paintAtc(), ms));
      });
    }

    /* Buy 1 is synthesised from the live unit price; the metafield supplies the rest. */
    buildTiers() {
      const { unit } = this.cfg;
      if (!unit) return [];
      const out = [{ qty: 1, each: unit, best: false }];

      (Array.isArray(this.cfg.tiers) ? this.cfg.tiers : []).forEach(t => {
        const qty = Number(t.qty);
        if (!qty || qty < 2) return;

        // "price" is in MAJOR units to match Shopify's Discounts screen.
        let each = null;
        if (t.price != null) each = Math.round(Number(t.price) * 100);
        else if (t.pct != null) each = Math.round(unit * (1 - Number(t.pct) / 100));
        if (each == null || !isFinite(each)) return;

        // Anything not actually cheaper is dropped rather than shown as a
        // "discount" - this is what catches a cents/dollars mix-up.
        if (each >= unit) return;

        out.push({ qty, each, best: !!t.best, freeShip: t.free_shipping === true });
      });

      out.sort((a, b) => a.qty - b.qty);
      return out;
    }

    money(cents) {
      const whole = cents % 100 === 0;
      const amount = whole ? String(Math.round(cents / 100)) : (cents / 100).toFixed(2);
      return '$' + amount;
    }

    render() {
      const L = this.cfg.labels;
      // Savings are measured against compare-at when there is one, so the figure
      // matches the saving the shopper sees on the price block.
      const base = this.cfg.compare > this.cfg.unit ? this.cfg.compare : this.cfg.unit;

      this.row.innerHTML = this.tiers.map((t, i) => {
        const total = t.each * t.qty;
        const save = base * t.qty - total;
        // Either the tier says so outright, or the basket clears the store's own
        // shipping threshold. The flag is the merchant's explicit statement, so it
        // is honoured - but it is an OR, not a replacement: a tier without the flag
        // still earns the badge once it crosses the threshold on value alone.
        const overThreshold = this.cfg.freeShipThreshold > 0 && total >= this.cfg.freeShipThreshold;
        const freeShip = this.cfg.showShip && (t.freeShip || overThreshold);
        return `
          <button type="button" role="radio" aria-checked="${i === this.selected}"
                  class="bold-bundle-save__tier${i === this.selected ? ' is-selected' : ''}${t.best ? ' is-best' : ''}"
                  data-bundle-tier="${i}" tabindex="${i === this.selected ? 0 : -1}">
            ${t.best ? `<span class="bold-bundle-save__badge">${L.best}</span>` : ''}
            <span class="bold-bundle-save__buy">${L.buy} ${t.qty}</span>
            <span class="bold-bundle-save__each">${this.money(t.each)}</span>
            <span class="bold-bundle-save__caption">${L.perItem}</span>
            ${this.cfg.showSave && save > 0 ? `<span class="bold-bundle-save__save">${L.save} ${this.money(save)}</span>` : ''}
            ${this.cfg.showTotal ? `<span class="bold-bundle-save__total">${this.money(total)} ${L.total}</span>` : ''}
            ${freeShip ? `<span class="bold-bundle-save__ship">${L.freeShip}</span>` : ''}
          </button>`;
      }).join('');

      this.paintQty();
      this.renderUnits();
      this.mountUnits();
      this.paintAtc();
    }

    /* The colour the product page itself is currently on. */
    currentColour() {
      const checked = document.querySelector(
        '.product__variant-picker .product-option__input:checked[name^="Colour"],' +
        '.product__variant-picker .product-option__input:checked[name^="Color"]'
      );
      return checked ? checked.value.trim() : (this.cfg.colours[0]?.name || '');
    }

    currentSize() {
      const checked = document.querySelector(
        '.product__variant-picker .product-option__input:checked:not([name^="Colour"]):not([name^="Color"])'
      );
      return checked ? checked.value.trim() : '';
    }

    /* One colour row per unit. Size stays shared - it comes from the product's own
       picker, as on the reference - so a row only chooses colour. */
    renderUnits() {
      if (!this.unitsEl || !this.cfg.perUnitColour) return;
      const qty = this.tiers[this.selected].qty;
      const L = this.cfg.labels;

      if (qty < 2 || !this.cfg.colours.length) {
        this.unitsEl.hidden = true;
        this.unitsEl.innerHTML = '';
        this.unitColours = [];
        return;
      }

      // Keep what the shopper already picked when the tier grows or shrinks.
      const fallback = this.currentColour();
      this.unitColours = Array.from({ length: qty },
        (_, i) => this.unitColours[i] || fallback);

      this.unitsEl.hidden = false;
      this.unitsEl.innerHTML = this.unitColours.map((chosen, i) => `
        <div class="bold-bundle-save__unit" data-unit-slot="${i}">
          <span class="bold-bundle-save__unit-label">
            ${this.cfg.colourName || 'Colour'} ${i + 1}:
            <strong data-unit-name>${chosen}</strong>
          </span>
          <div class="bold-bundle-save__unit-swatches" role="radiogroup"
               aria-label="${this.cfg.colourName || 'Colour'} ${i + 1}">
            ${this.cfg.colours.map(c => `
              <button type="button" role="radio"
                      aria-checked="${c.name === chosen}"
                      aria-label="${c.name}"
                      title="${c.name}"
                      class="bold-bundle-save__unit-sw bold-sw--${c.handle}${c.name === chosen ? ' is-selected' : ''}"
                      data-unit-swatch="${c.name}"></button>`).join('')}
          </div>
        </div>`).join('');
    }

    /* HAUS puts the per-unit pickers where the product's own colour row is and
       hides that row, so there is one place to choose a colour rather than two.
       Same here: the slots are moved into the variant picker, between the colour
       and size fieldsets, and the colour fieldset is hidden while they are up.
       The radios inside it stay in the DOM and keep driving the gallery - they
       are only hidden, so pickColour() still works through them.

       Re-runnable: a variant change can re-render product-variant-selects and
       take the slots out with it, so the observer calls this again. Everything
       is compared before it is written, so re-entry stops on the second pass. */
    mountUnits() {
      if (!this.unitsEl) return;
      const selects = document.querySelector('.product__variant-picker product-variant-selects');
      if (!selects) return;

      const colour = [...selects.querySelectorAll('fieldset.product__variant-options')]
        .find(f => /^colou?r/i.test((f.querySelector('.product-option__input')?.name || '').trim()));
      if (!colour) return;

      if (this.unitsEl.previousElementSibling !== colour) {
        colour.insertAdjacentElement('afterend', this.unitsEl);
      }
      const active = !this.unitsEl.hidden;
      if (colour.classList.contains('bold-units-replaced') !== active) {
        colour.classList.toggle('bold-units-replaced', active);
      }
    }

    /* Picking any unit's colour also drives the product's own picker, so the main
       gallery moves to that colourway - the shopper sees the colour they just
       touched. Driving the real radio reuses the existing media filtering rather
       than duplicating it. */
    pickColour(slot, name) {
      this.unitColours[slot] = name;

      const row = this.unitsEl.querySelector(`[data-unit-slot="${slot}"]`);
      row.querySelector('[data-unit-name]').textContent = name;
      row.querySelectorAll('[data-unit-swatch]').forEach(b => {
        const on = b.dataset.unitSwatch === name;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', String(on));
      });

      const input = [...document.querySelectorAll('.product__variant-picker .product-option__input')]
        .find(i => /^colou?r/i.test(i.name.trim()) && i.value.trim() === name);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.paintAtc();
    }

    variantFor(colour, size) {
      return (this.cfg.variants || []).find(v =>
        v.colour === colour && (!size || v.size === size));
    }

    /* A bundle of mixed colours is several line items, which the theme's own form
       cannot express - it carries one variant id. Intercepted and posted directly.

       Single-colour bundles are posted here too, rather than left to the form.
       The form's only record of the quantity is the hidden quantity field, and
       the theme resets that field to 1 every time it re-renders the buy block on
       a variant change. refresh() re-asserts it, but the cart should not depend
       on a field the theme owns and rewrites: a stale 1 would silently add one
       item to a shopper who chose Buy 3. Counting the unit slots is the only
       record that cannot drift out of step with what the widget shows. */
    onSubmit(e) {
      const qty = this.tiers[this.selected].qty;
      if (qty < 2 || !this.cfg.perUnitColour || !this.unitColours.length) return;

      const size = this.currentSize();
      const counts = new Map();
      for (const c of this.unitColours) {
        const v = this.variantFor(c, size);
        if (!v) return;                       // unresolvable: let the form do its thing
        counts.set(v.id, (counts.get(v.id) || 0) + 1);
      }

      e.preventDefault();
      e.stopImmediatePropagation();   // keep product-form from posting it a second time
      const items = [...counts].map(([id, quantity]) => ({ id, quantity }));
      const cfg = typeof fetchConfig === 'function' ? fetchConfig('json') : {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      };
      // Ask for the drawer's own sections in the same round trip, then hand the
      // response to renderContents() - the contract cart.js uses itself. Posting
      // raw and redirecting to /cart worked, but it threw the shopper out of the
      // page instead of sliding the cart open like every other add on the site.
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map(s => s.section) || [];
      cfg.body = JSON.stringify(sections.length
        ? { items, sections, sections_url: window.location.pathname }
        : { items });

      fetch((typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js', cfg)
        .then(r => r.json())
        .then(res => {
          if (res.status) throw new Error(res.description || res.message || 'add failed');
          if (drawer && res.sections) drawer.renderContents(res);
          else window.location.href = '/cart';
        })
        .catch(() => this.form.submit());
    }

    /* "Add to cart • $96 · Save $164" once a size is picked. The total is what the
       selected tier actually costs (qty x tier price); the saving is measured
       against compare-at, the same figure the tier card shows. Skipped while
       bold-select-size is holding the button at "Select size", so the two do not
       fight over it. Writes only when the label actually changes - it is called
       from a MutationObserver, and an unconditional write would loop. */
    paintAtc() {
      const btn = document.querySelector('.product-form__submit');
      if (!btn || btn.disabled || btn.dataset.boldSelectSize === 'true') return;
      const span = btn.querySelector('span') || btn;
      const t = this.tiers[this.selected];
      if (!t) return;
      const total = t.each * t.qty;
      const base = (this.cfg.compare > this.cfg.unit ? this.cfg.compare : this.cfg.unit) * t.qty;
      const save = base - total;
      const label = (window.variantStrings?.addToCart || 'Add to cart');
      const key = `${label}|${total}|${save}`;
      if (btn.dataset.boldAtc === key && span.querySelector('.bold-atc-price')) return;

      // Escaped because the label is theme-supplied; the money figures are generated here.
      const esc = str => String(str).replace(/[&<>"]/g,
        c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const sep = '<span class="bold-atc-price__sep" aria-hidden="true">\u2022</span>';
      span.innerHTML = `${esc(label)}${sep}<span class="bold-atc-price">` +
        `<span class="bold-atc-price__now">${this.money(total)}</span>` +
        (save > 0
          ? `<span class="bold-atc-price__save" style="margin-inline-start:.6em;opacity:.9;">\u00b7 Save ${this.money(save)}</span>`
          : '') +
        `</span>`;
      btn.dataset.boldAtc = key;
    }

    paintQty() {
      const L = this.cfg.labels;
      const q = this.tiers[this.selected].qty;
      if (this.qtyEl) this.qtyEl.textContent = `${L.quantity}: ${q} ${q === 1 ? L.item : L.items}`;
    }

    select(i, focus) {
      if (i === this.selected || !this.tiers[i]) return;
      this.selected = i;

      this.row.querySelectorAll('[data-bundle-tier]').forEach((b, n) => {
        const on = n === i;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', String(on));
        b.tabIndex = on ? 0 : -1;
        if (on && focus) b.focus();
      });

      this.paintQty();
      this.renderUnits();
      this.mountUnits();
      this.paintAtc();
      this.syncQuantity(this.tiers[i].qty);
    }

    /* The only thing that actually reaches the cart. */
    syncQuantity(qty) {
      const input = document.getElementById(this.dataset.quantityInput)
        || document.querySelector('.product-form__quantity input[name="quantity"]');
      if (!input || input.value === String(qty)) return;
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Variant changed: re-read the price and rebuild, keeping the chosen qty. */
    refresh() {
      const picker = document.querySelector('.product__variant-picker');
      if (!picker) return;
      const priceEl = document.querySelector('[data-bundle-save-unit]');
      const fresh = priceEl ? Number(priceEl.dataset.bundleSaveUnit) : null;
      if (fresh && fresh !== this.cfg.unit) {
        this.cfg.unit = fresh;
        const wanted = this.tiers[this.selected]?.qty ?? 1;
        this.tiers = this.buildTiers();
        const idx = this.tiers.findIndex(t => t.qty === wanted);
        this.selected = idx >= 0 ? idx : 0;
        this.render();
      }

      // The theme re-renders the buy block on every variant change and the fresh
      // markup carries quantity="1", so a shopper who picked Buy 3 and then
      // changed colour was left with a field reading 1. The price does not move
      // between colourways, so the branch above does not run - re-assert both
      // regardless of whether the ladder was rebuilt.
      this.syncQuantity(this.tiers[this.selected]?.qty ?? 1);
      this.paintAtc();
    }
  }

  if (!customElements.get('bold-bundle-save')) customElements.define('bold-bundle-save', BoldBundleSave);
})();